/**
 * Risk Table Data Helpers
 * Utilities for extracting and calculating numbers at risk from API responses
 */

/**
 * Extract numbers at risk, censored, and events from KM object for specific time points.
 * All three arrays are read directly from the backend response — no client-side estimation.
 *
 * @param {Object} kmObject - KM object from API with x, y, at_risk, events, censored arrays
 * @param {Array} targetTimePoints - Time points to display (e.g., [0, 3, 6, 9, 12, 15, 18, 24])
 * @returns {Object|null} - { atRisk, censored, events } arrays, or null if data is invalid
 */
export const extractRiskDataFromKM = (kmObject, targetTimePoints = [0, 3, 6, 9, 12, 15, 18, 24]) => {
  // Silently return null for invalid objects during initial renders
  if (!kmObject || !kmObject.x || !kmObject.y) {
    return null;
  }

  if (!kmObject.at_risk || !Array.isArray(kmObject.at_risk) || kmObject.at_risk.length === 0) {
    return null;
  }

  const {
    x: timePoints,
    at_risk: atRiskData,
    events: eventsData,
    censored: censoredData,
  } = kmObject;

  /**
   * Look up the value for a target time point from a backend array.
   * - If the exact month exists in x, return that index's value.
   * - Otherwise fall back to the last value before targetTime (step-function behaviour).
   */
  const lookupAtTime = (dataArray, targetTime) => {
    if (!dataArray || !Array.isArray(dataArray)) return 0;
    const index = timePoints.indexOf(targetTime);
    if (index !== -1) return dataArray[index] ?? 0;
    // Use manual reverse loop for browser compatibility
    let closestIndex = -1;
    for (let i = timePoints.length - 1; i >= 0; i--) {
      if (timePoints[i] <= targetTime) { closestIndex = i; break; }
    }
    return closestIndex >= 0 ? (dataArray[closestIndex] ?? 0) : 0;
  };

  const atRisk   = [];
  const events   = [];
  const censored = [];

  // Backend now always returns full 0-24 month data (25 points).
  // Simply show all actual values from the API at each target time point.
  for (const tp of targetTimePoints) {
    atRisk.push(lookupAtTime(atRiskData, tp));
    events.push(lookupAtTime(eventsData, tp));
    censored.push(lookupAtTime(censoredData, tp));
  }

  return { atRisk, censored, events };

  /* ─── OLD BOUNDARY/TRUNCATION LOGIC (commented out — backend no longer truncates) ───
  const maxTimeInData = timePoints[timePoints.length - 1];

  // If the API provides full data (x extends to 24 months), show ALL actual values
  if (maxTimeInData >= 24) {
    for (const tp of targetTimePoints) {
      atRisk.push(lookupAtTime(atRiskData, tp));
      events.push(lookupAtTime(eventsData, tp));
      censored.push(lookupAtTime(censoredData, tp));
    }
    return { atRisk, censored, events };
  }

  // Short data (backend truncated the curve) — use boundary logic
  let lastNonZeroAtRiskTime = 0;
  for (let i = timePoints.length - 1; i >= 0; i--) {
    if (atRiskData[i] > 0) {
      lastNonZeroAtRiskTime = timePoints[i];
      break;
    }
  }

  let extraBoundaryTimePoint = null;
  for (const tp of targetTimePoints) {
    if (tp > maxTimeInData) {
      extraBoundaryTimePoint = tp;
      break;
    }
  }

  const initialAtRisk = atRiskData[0] ?? 0;

  for (const tp of targetTimePoints) {
    if (tp <= maxTimeInData) {
      atRisk.push(lookupAtTime(atRiskData, tp));
      events.push(lookupAtTime(eventsData, tp));
      censored.push(lookupAtTime(censoredData, tp));
    } else if (tp === extraBoundaryTimePoint) {
      atRisk.push(0);
      censored.push(0);
      events.push(initialAtRisk);
    } else {
      atRisk.push(null);
      censored.push(null);
      events.push(null);
    }
  }

  return { atRisk, censored, events };
  ─── END OLD LOGIC ─── */
};

/**
 * Build cohort data for RiskTable component from KM objects
 * @param {Object} options - Configuration object
 * @param {Object} options.overallKM - Overall cohort KM object
 * @param {Object} options.personaKM - Selected persona KM object
 * @param {Array} options.linesOfFailureKM - Array of lines of failure KM objects
 * @param {Object} options.visibility - Visibility flags for each line type
 * @param {Array} options.timePoints - Time points to display
 * @returns {Array} - Array of cohort objects for RiskTable
 */
export const buildRiskTableCohorts = ({
  overallKM = null,
  personaKM = null,
  linesOfFailureKM = [],
  visibility = { overallCohort: true, selectedPersona: true, linesOfFailure: true },
  timePoints = [0, 3, 6, 9, 12, 15, 18, 24]
}) => {
  const cohorts = [];

  // Overall Cohort (gray dotted line)
  if (visibility.overallCohort && overallKM) {
    const riskData = extractRiskDataFromKM(overallKM, timePoints);
    if (riskData) {
      cohorts.push({
        id: 'overall',
        name: overallKM.label || 'Overall Cohort',
        color: '#94a3b8', // Gray
        visible: true,
        atRisk: riskData.atRisk,
        censored: riskData.censored,
        events: riskData.events
      });
    }
  }

  // Selected Persona (blue solid line)
  if (visibility.selectedPersona && personaKM) {
    const riskData = extractRiskDataFromKM(personaKM, timePoints);
    if (riskData) {
      cohorts.push({
        id: 'persona',
        name: personaKM.label || 'Selected Persona',
        color: '#0EA5E9', // Blue
        visible: true,
        atRisk: riskData.atRisk,
        censored: riskData.censored,
        events: riskData.events
      });
    }
  }

  // Lines of Failure (multiple colored lines)
  if (visibility.linesOfFailure && linesOfFailureKM && linesOfFailureKM.length > 0) {
    const lineColors = ['#10B981', '#F59E0B', '#8B5CF6', '#EF4444']; // Green, Amber, Purple, Red
    
    linesOfFailureKM.forEach((lineKM, idx) => {
      if (lineKM && lineKM.kmData && lineKM.n > 0) {
        const riskData = extractRiskDataFromKM(lineKM.kmData, timePoints);
        if (riskData) {
          cohorts.push({
            id: `line_${lineKM.id || idx}`,
            name: lineKM.label || `Line ${idx + 1}`,
            color: lineColors[idx % lineColors.length],
            visible: true,
            atRisk: riskData.atRisk,
            censored: riskData.censored,
            events: riskData.events
          });
        }
      }
    });
  }

  return cohorts;
};

/**
 * Validate KM object structure
 * @param {Object} kmObject - KM object to validate
 * @returns {boolean} - True if valid
 */
export const isValidKMObject = (kmObject) => {
  if (!kmObject) return false;

  const hasRequiredFields =
    Array.isArray(kmObject.x) &&
    Array.isArray(kmObject.y) &&
    Array.isArray(kmObject.at_risk) &&
    Array.isArray(kmObject.events) &&
    Array.isArray(kmObject.censored);

  if (!hasRequiredFields) return false;

  return (
    kmObject.x.length === kmObject.y.length &&
    kmObject.x.length === kmObject.at_risk.length
  );
};
